import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  createFoldersCollection as createFoldersCollectionCore,
  createViewsCollection as createViewsCollectionCore,
  type SupportTicketViewFolderApi,
} from "@redotech/redo-support-v2/query/support-ticket-view-folder-collections";
import type { QueryClient } from "@tanstack/react-query";
import { catchApiErrors } from "./error-handling";

interface CollectionOptions {
  queryClient: QueryClient;
  effectApi: EffectApiContext;
}

function createViewFolderApi(
  effectApi: EffectApiContext,
): SupportTicketViewFolderApi {
  const client = effectApi.getClient();
  return {
    getViewsAndFolders: () =>
      effectApi.runPromise(
        catchApiErrors(client.viewFolder.getViewsAndFolders({})),
      ),
    createView: (payload) =>
      effectApi
        .runPromise(catchApiErrors(client.viewFolder.createView({ payload })))
        .then(() => undefined),
    updateView: (id, payload) =>
      effectApi.runPromise(
        catchApiErrors(client.viewFolder.updateView({ path: { id }, payload })),
      ),
    deleteView: (id) =>
      effectApi.runPromise(
        catchApiErrors(client.viewFolder.deleteView({ path: { id } })),
      ),
    createFolder: (payload) =>
      effectApi
        .runPromise(catchApiErrors(client.viewFolder.createFolder({ payload })))
        .then(() => undefined),
    updateFolder: (id, payload) =>
      effectApi.runPromise(
        catchApiErrors(
          client.viewFolder.updateFolder({ path: { id }, payload }),
        ),
      ),
    deleteFolder: (id) =>
      effectApi.runPromise(
        catchApiErrors(client.viewFolder.deleteFolder({ path: { id } })),
      ),
  };
}

export function createViewsCollection({
  queryClient,
  effectApi,
}: CollectionOptions) {
  return createViewsCollectionCore({
    queryClient,
    api: createViewFolderApi(effectApi),
  });
}

export function createFoldersCollection({
  queryClient,
  effectApi,
}: CollectionOptions) {
  return createFoldersCollectionCore({
    queryClient,
    api: createViewFolderApi(effectApi),
  });
}

export {
  supportTicketFolderQueryKeys as folderQueryKeys,
  supportTicketViewQueryKeys as viewQueryKeys,
  type FoldersCollection,
  type ViewsCollection,
} from "@redotech/redo-support-v2/query/support-ticket-view-folder-collections";
